import cx from 'classnames';
import BaseButton, { BaseButtonProps } from 'components/base-button';
import { useHasComponentRendered } from 'lib/hooks/useHasComponentRendered';
import { useCallback, useEffect, useRef, useState } from 'react';

type ButtonProps = {
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  isDarkBackground?: boolean;
};

// minimum time to show loader 300 + 400 + 300 (animation in + showing + animation out)
const MINIMUM_LOADING_DURATION = 1000;
const CHECKMARK_DURATION = 1500;

const Button = ({
  ref: buttonRef,
  children,
  className = '',
  disabled,
  fullWidth,
  icon,
  iconPosition,
  loading,
  small,
  text,
  type = 'button',
  variant,
  isDarkBackground,
  ...props
}: ButtonProps &
  BaseButtonProps &
  React.HTMLAttributes<HTMLButtonElement> & {
    ref?: React.RefObject<HTMLButtonElement | null>;
  }) => {
  const [showLoading, setShowLoading] = useState(loading);
  const timeLoadingStarted = useRef(0);
  const isRendered = useHasComponentRendered();
  const [success, setSuccess] = useState(false);
  const successTimeout = useRef(0);
  const loaderTimeout = useRef(0);

  const hideLoading = () => {
    setShowLoading(false);
    setSuccess(true);
    successTimeout.current = window.setTimeout(() => {
      setSuccess(false);
    }, CHECKMARK_DURATION);
  };

  const handleLoader = useCallback(loading => {
    const currentTime = new Date().getTime();
    if (loading) {
      timeLoadingStarted.current = currentTime;
      setShowLoading(loading);
    } else {
      const timeSinceLoadingStarted = currentTime - timeLoadingStarted.current;
      if (timeSinceLoadingStarted < MINIMUM_LOADING_DURATION) {
        loaderTimeout.current = window.setTimeout(() => {
          hideLoading();
        }, MINIMUM_LOADING_DURATION - timeSinceLoadingStarted);
      } else {
        hideLoading();
      }
      timeLoadingStarted.current = 0;
    }
  }, []);

  useEffect(() => {
    if (isRendered) {
      handleLoader(loading);
    }
    return function cleanup() {
      clearTimeout(successTimeout.current);
      clearTimeout(loaderTimeout.current);
    };
    // We leave isRendered out of the dependencies because only the change of loading
    // should trigger handleLoader. It is safe to leave isRendered out of dependencies because
    // we change it only on mount, and other dependencies can't change in between.
  }, [loading, handleLoader]);

  const buttonClasses = cx('rounded-sm', className, {
    'w-full': fullWidth,
    'pointer-events-none': showLoading || success || disabled
  });

  return (
    <button
      ref={buttonRef}
      className={buttonClasses}
      type={type}
      disabled={disabled || showLoading || success}
      {...props}
    >
      <BaseButton
        interactive
        disabled={disabled}
        text={text}
        small={small}
        variant={variant}
        icon={icon}
        iconPosition={iconPosition}
        fullWidth={fullWidth}
        isDarkBackground={isDarkBackground}
        loading={showLoading}
        success={success}
      >
        {children}
      </BaseButton>
    </button>
  );
};

export default Button;
